export type MenuAlignment = 'center' | 'left' | 'right';
export type MenuSide = 'bottom' | 'top';

export const MENU_ANIMATION_DURATION_MS = 160;
export const MENU_ANIMATION_INITIAL_SCALE = 0.98;
export const MENU_ANIMATION_OFFSET = 4;

export type MenuRectangle = {
  height: number;
  width: number;
  x: number;
  y: number;
};

export type MenuSize = {
  height: number;
  width: number;
};

export type AnchoredMenuLayout = {
  maxHeight: number;
  resolvedSide: MenuSide;
  start: number;
  top: number;
};

type GetAnchoredMenuLayoutOptions = {
  align: MenuAlignment;
  anchor: MenuRectangle;
  gap?: number;
  isRTL?: boolean;
  margin?: number;
  preferredSide: MenuSide;
  surface: MenuSize;
  viewport: MenuSize;
};

export function getRectangleRelativeToViewport({
  rectangle,
  viewport,
}: {
  rectangle: MenuRectangle;
  viewport: MenuRectangle;
}): MenuRectangle {
  return {
    height: rectangle.height,
    width: rectangle.width,
    x: rectangle.x - viewport.x,
    y: rectangle.y - viewport.y,
  };
}

export function getAnchoredMenuLayout({
  align,
  anchor,
  gap = 8,
  isRTL = false,
  margin = 8,
  preferredSide,
  surface,
  viewport,
}: GetAnchoredMenuLayoutOptions): AnchoredMenuLayout {
  const belowTop = anchor.y + anchor.height + gap;
  const aboveBottom = anchor.y - gap;
  const availableBelow = Math.max(0, viewport.height - margin - belowTop);
  const availableAbove = Math.max(0, aboveBottom - margin);
  const preferredAvailable =
    preferredSide === 'bottom' ? availableBelow : availableAbove;
  const oppositeAvailable =
    preferredSide === 'bottom' ? availableAbove : availableBelow;
  const resolvedSide =
    preferredAvailable >= surface.height ||
    preferredAvailable >= oppositeAvailable
      ? preferredSide
      : oppositeSide(preferredSide);
  const maxHeight = resolvedSide === 'bottom' ? availableBelow : availableAbove;
  const renderedHeight = Math.min(surface.height, maxHeight);

  const desiredLeft = getDesiredLeft({ align, anchor, isRTL, surface });
  const maxLeft = Math.max(margin, viewport.width - margin - surface.width);
  const left = clamp(desiredLeft, margin, maxLeft);
  const start = isRTL ? viewport.width - left - surface.width : left;
  const desiredTop =
    resolvedSide === 'bottom' ? belowTop : aboveBottom - renderedHeight;
  const maxTop = Math.max(margin, viewport.height - margin - renderedHeight);
  const top = clamp(desiredTop, margin, maxTop);

  return {
    maxHeight,
    resolvedSide,
    start,
    top,
  };
}

function getDesiredLeft({
  align,
  anchor,
  isRTL,
  surface,
}: Pick<
  GetAnchoredMenuLayoutOptions,
  'align' | 'anchor' | 'isRTL' | 'surface'
>) {
  if (align === 'right') {
    return isRTL ? anchor.x : anchor.x + anchor.width - surface.width;
  }

  if (align === 'center') {
    return anchor.x + (anchor.width - surface.width) / 2;
  }

  return isRTL ? anchor.x + anchor.width - surface.width : anchor.x;
}

function oppositeSide(side: MenuSide): MenuSide {
  return side === 'bottom' ? 'top' : 'bottom';
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}
